/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal, Checkbox, Input, message, Row, Col,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import Select from 'components/Select';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';

const formatFecha = (fecha) => {
  if (!fecha) return '';
  const [anio, mes, dia] = fecha.split('-');
  return `${dia}/${mes}/${anio}`;
};

// Tabla local (sin backend propio: los candidatos ya vienen calculados del padre, ver
// RetroactivoDetalleTab), igual que esa tabla -- permiso siempre completo, no depende del
// modelo de permisos de Django.
const PERMISO_COMPLETO = {
  model: 'retroactivocandidato',
  add: true,
  change: true,
  delete: true,
  hasAllViewPermission: true,
};

/**
 * Selector de candidatos a pago de retroactivo (se abre desde el botón "Agregar" del tab
 * Retroactivos -- ver RetroactivoDetalleTab). A diferencia del selector anterior, cada
 * renglón es un par Empleado+Periodo independiente (no se agrupan por empleado): así se
 * puede agregar, por ejemplo, solo el periodo más reciente de un empleado aunque tenga
 * varios periodos previos también pendientes.
 *
 * No hace fetch propio: recibe `candidatos` ya calculados por el padre (un solo fetch por
 * sesión de captura, ver RetroactivoDetalleTab) para no repetir la consulta cada vez que se
 * abre el selector.
 */
const RetroactivoAgregarModal = ({
  open, onCancel, onConfirm, candidatos, filasYaAgregadas, sujetoLabel,
}) => {
  const [busquedaEmpleado, setBusquedaEmpleado] = useState('');
  const [busquedaPlaza, setBusquedaPlaza] = useState('');
  const [periodosSeleccionados, setPeriodosSeleccionados] = useState([]);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [currentTableData, setCurrentTableData] = useState([]);

  useEffect(() => {
    if (open) {
      setBusquedaEmpleado('');
      setBusquedaPlaza('');
      setPeriodosSeleccionados([]);
      setSeleccionados(new Set());
      setCurrentTableData([]);
    }
  }, [open]);

  // Candidatos aplanados (un renglón por Empleado+Periodo) menos los que ya están
  // agregados en la tabla principal del tab.
  const filasDisponibles = useMemo(() => {
    const resultado = [];
    (candidatos || []).forEach((candidato) => {
      candidato.periodos.forEach((periodo) => {
        const key = `${candidato.administracion_de_plaza_id}-${periodo.periodo_de_nomina_detalle_id}`;
        if (filasYaAgregadas?.has(key)) return;
        resultado.push({
          key,
          administracionDePlazaId: candidato.administracion_de_plaza_id,
          empleadoId: candidato.empleado_id,
          numeroDeEmpleado: candidato.numero_de_empleado,
          nombreEmpleado: candidato.nombre_empleado,
          plazaId: candidato.plaza_id,
          plazaClaveYDescripcion: candidato.plaza_clave_y_descripcion,
          fechaAsignacion: candidato.fecha_asignacion,
          periodoId: periodo.periodo_de_nomina_detalle_id,
          numeroDeNomina: periodo.numero_de_nomina,
          fechaInicial: periodo.fecha_inicial,
          fechaFinal: periodo.fecha_final,
          numeroDeDiasPeriodo: periodo.numero_de_dias_periodo,
          diasNoLaboradosIncidencia: periodo.dias_no_laborados_incidencia,
          numeroDeDias: periodo.numero_de_dias,
        });
      });
    });
    return resultado;
  }, [candidatos, filasYaAgregadas]);

  // Opciones del multiselector de Periodo: se derivan de los propios candidatos ya
  // calculados (filasDisponibles), nunca del catálogo completo de periodos -- el backend
  // (retroactivo_candidatos.py) ya los limita a los periodos anteriores al seleccionado en
  // el tab Detalle Nómina, así que esta lista queda automáticamente acotada igual.
  const periodosDisponibles = useMemo(() => {
    const porId = new Map();
    filasDisponibles.forEach((fila) => {
      if (!porId.has(fila.periodoId)) {
        porId.set(fila.periodoId, {
          id: fila.periodoId,
          numeroDeNomina: fila.numeroDeNomina,
          fechaInicial: fila.fechaInicial,
          fechaFinal: fila.fechaFinal,
        });
      }
    });
    return Array.from(porId.values()).sort((a, b) => (a.fechaInicial < b.fechaInicial ? -1 : 1));
  }, [filasDisponibles]);

  const filasFiltradas = useMemo(() => {
    const textoEmpleado = busquedaEmpleado.trim().toLowerCase();
    const textoPlaza = busquedaPlaza.trim().toLowerCase();
    return filasDisponibles.filter((fila) => {
      const coincideEmpleado = !textoEmpleado || `${fila.numeroDeEmpleado} ${fila.nombreEmpleado}`
        .toLowerCase().includes(textoEmpleado);
      const coincidePlaza = !textoPlaza || (fila.plazaClaveYDescripcion || '')
        .toLowerCase().includes(textoPlaza);
      const coincidePeriodo = !periodosSeleccionados.length
        || periodosSeleccionados.includes(fila.periodoId);
      return coincideEmpleado && coincidePlaza && coincidePeriodo;
    });
  }, [filasDisponibles, busquedaEmpleado, busquedaPlaza, periodosSeleccionados]);

  // Patrón obligatorio de selección en tablas (CLAUDE.md): Checkbox propio en columnTitle
  // que lee siempre del dataset filtrado/visible (effectiveData), nunca onSelectAll de AntD
  // (que solo opera sobre los renglones de la página actual).
  const effectiveData = currentTableData.length ? currentTableData : filasFiltradas;

  const handleTableChange = (_pagination, _filters, _sorter, extra) => {
    if (extra?.currentDataSource) setCurrentTableData(extra.currentDataSource);
  };

  const rowSelection = {
    selectedRowKeys: Array.from(seleccionados),
    type: 'checkbox',
    columnTitle: (
      <Checkbox
        checked={effectiveData.length > 0 && seleccionados.size === effectiveData.length}
        indeterminate={seleccionados.size > 0 && seleccionados.size < effectiveData.length}
        onChange={(e) => {
          const nuevaSeleccion = e.target.checked ? effectiveData.map((fila) => fila.key) : [];
          setSeleccionados(new Set(nuevaSeleccion));
        }}
      />
    ),
    onSelect: (record, selected) => {
      setSeleccionados((prev) => {
        const nuevo = new Set(prev);
        if (selected) nuevo.add(record.key); else nuevo.delete(record.key);
        return nuevo;
      });
    },
    onChange: (keys) => setSeleccionados(new Set(keys)),
  };

  // El checkbox de cada renglón no es clicable directamente: regla global del sistema
  // (Layout/index.scss) -- "tbody .ant-table-cell.ant-table-selection-column label
  // { pointer-events: none }" -- para que un clic no dispare a la vez el onChange del
  // checkbox y el onClick de la fila. La selección se activa con el mismo mecanismo que
  // el resto de las tablas del sistema: handleOnRowClick (ver AdministracionDePlazas.jsx).
  const handleOnRowClick = (record) => {
    setSeleccionados((prev) => {
      const nuevo = new Set(prev);
      if (nuevo.has(record.key)) nuevo.delete(record.key); else nuevo.add(record.key);
      return nuevo;
    });
  };

  const handleConfirmar = () => {
    if (!seleccionados.size) {
      message.warning('Selecciona al menos un periodo.');
      return;
    }
    const filasElegidas = filasDisponibles.filter((fila) => seleccionados.has(fila.key));
    onConfirm(filasElegidas);
  };

  const columnas = [
    {
      titleText: sujetoLabel || 'Empleado',
      dataIndex: 'nombreEmpleado',
    },
    {
      titleText: 'Plaza',
      dataIndex: 'plazaClaveYDescripcion',
    },
    {
      titleText: 'Fecha de Asignación',
      dataIndex: 'fechaAsignacion',
      render: (valor) => formatFecha(valor),
    },
    {
      titleText: 'Periodo',
      dataIndex: 'numeroDeNomina',
      render: (valor, fila) => `${fila.numeroDeNomina} (Del ${formatFecha(fila.fechaInicial)} al ${formatFecha(fila.fechaFinal)})`,
    },
    {
      titleText: 'Días del Periodo',
      dataIndex: 'numeroDeDiasPeriodo',
      width: 110,
      align: 'center',
    },
    {
      titleText: 'Días No Laborados',
      dataIndex: 'diasNoLaboradosIncidencia',
      width: 130,
      align: 'center',
      render: (valor) => (valor > 0 ? valor : '-'),
    },
    {
      titleText: 'Días Sugeridos',
      dataIndex: 'numeroDeDias',
      width: 110,
      align: 'center',
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      width={1000}
      footer={null}
      closable={false}
      title={(
        <FormSubmitControls
          label="Agregar Empleados y Periodos de Retroactivo"
          onCancel={onCancel}
          onCancelText="Cancelar"
          onFinish={handleConfirmar}
          onFinishText={`Agregar seleccionados (${seleccionados.size})`}
          onFinishIcon={<PlusOutlined />}
        />
      )}
    >
      <Row gutter={10} style={{ marginBottom: 10 }}>
        <Col xs={24} sm={8}>
          <Input
            allowClear
            placeholder={`Buscar por ${(sujetoLabel || 'Empleado').toLowerCase()}`}
            value={busquedaEmpleado}
            onChange={(e) => setBusquedaEmpleado(e.target.value)}
          />
        </Col>
        <Col xs={24} sm={8}>
          <Input
            allowClear
            placeholder="Buscar por Plaza"
            value={busquedaPlaza}
            onChange={(e) => setBusquedaPlaza(e.target.value)}
          />
        </Col>
        <Col xs={24} sm={8}>
          <Select
            mode="multiple"
            valueProp="id"
            dataSource={periodosDisponibles}
            value={periodosSeleccionados}
            onChange={(val) => setPeriodosSeleccionados(val || [])}
            placeholder="Buscar por periodo"
            render={(item) => `${item.numeroDeNomina} (${formatFecha(item.fechaInicial)} al ${formatFecha(item.fechaFinal)})`}
          />
        </Col>
      </Row>
      <Table
        rowKey="key"
        cols={columnas}
        data={filasFiltradas}
        count={filasFiltradas.length}
        titleTable="Candidatos a Retroactivo"
        permission={PERMISO_COMPLETO}
        rowSelection={rowSelection}
        handleOnRowClick={handleOnRowClick}
        controls={{}}
        allowEdit={false}
        allowUnasign={false}
        allowSearch={false}
        allowMultiSelect
        allowMultiDeselect
        onMultiSelect={() => setSeleccionados(new Set(effectiveData.map((fila) => fila.key)))}
        onMultiDeselect={() => setSeleccionados(new Set())}
        onTableChange={handleTableChange}
      />
    </Modal>
  );
};

export default RetroactivoAgregarModal;
